const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

export type PlannedShipmentStatus =
  | 'pendingDeparture'
  | 'inTransit'
  | 'expectedToday'
  | 'etaOverdue'
  | 'missingEta';

export type ShipmentLogisticsStatus =
  | 'pendingDeparture'
  | 'inTransit'
  | 'arrived'
  | 'missingEta';

export type ShipmentManualSortField =
  | 'client'
  | 'reference'
  | 'container'
  | 'kilos'
  | 'logisticsStatus'
  | 'etd'
  | 'eta'
  | 'vessel'
  | 'destination'
  | 'apiTracking'
  | 'settlementStatus'
  | 'settlementDate';

export type ShipmentSortDirection = 'ascending' | 'descending';

// Ocean (DCSA) and air (IATA Cargo-IMP) milestone codes.
export const preDepartureTrackingEvents = new Set([
  'EMSH',
  'GTIN',
  'STUF',
  'BKD',
  'RCS',
  'FOH',
  'MAN',
]);
export const inTransitTrackingEvents = new Set(['LOAD', 'DEPA', 'DEP']);
// Arrival events still need to match the destination; an air ARR/RCF can be
// a transit airport.
export const arrivedTrackingEvents = new Set([
  'ARRV',
  'DISC',
  'GTOT',
  'STRP',
  'EMPTY_RETURN',
  'ARR',
  'RCF',
  'NFD',
  'AWD',
  'DLV',
]);

interface TrackingJourneyLogisticsSnapshot {
  observation_status?: string | null;
  provider_container_status?: string | null;
  live_etd?: string | null;
  live_eta?: string | null;
  destination_name?: string | null;
  destination_locode?: string | null;
  latest_actual_event?: {
    event_type: string;
    occurred_at?: string | null;
    vessel_name?: string | null;
    location_name?: string | null;
    location_locode?: string | null;
  } | null;
}

interface ShipmentLogisticsSnapshot {
  ship_etd?: string | null;
  ship_eta?: string | null;
  ship_destination_port?: string | null;
  tracking_journeys?: TrackingJourneyLogisticsSnapshot[];
}

interface SortableShipmentLogisticsSnapshot extends ShipmentLogisticsSnapshot {
  reference_number?: string | null;
  receiver?: string | null;
  container_numbers?: string[];
  total_kilos?: number | null;
  ship_name?: string | null;
  estimated_settlement_date?: Date | string | null;
  status?: 'settled' | 'pending' | null;
}

const normalizeLocode = (value?: string | null) => {
  const normalized = (value || '').toUpperCase().replace(/[\s-]+/g, '');
  return /^[A-Z]{2}[A-Z0-9]{3}$/.test(normalized) ? normalized : null;
};

const normalizeLocationName = (value?: string | null) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();

const isLatestActualEventAtDestination = (
  journey: TrackingJourneyLogisticsSnapshot,
  shipment: ShipmentLogisticsSnapshot
) => {
  const event = journey.latest_actual_event;
  if (!event) return false;

  const destinationLocode =
    normalizeLocode(journey.destination_locode) ||
    normalizeLocode(shipment.ship_destination_port);
  const eventLocode = normalizeLocode(event.location_locode);

  // LOCODEs are authoritative when both sides provide them.
  if (destinationLocode && eventLocode) {
    return destinationLocode === eventLocode;
  }

  const destinationName = normalizeLocationName(
    journey.destination_name || shipment.ship_destination_port
  );
  const eventName = normalizeLocationName(event.location_name);
  if (destinationName && eventName) {
    return (
      destinationName === eventName ||
      destinationName.includes(eventName) ||
      eventName.includes(destinationName)
    );
  }

  // Preserve legacy behaviour only when the shipment has no destination data
  // against which the actual event can be checked.
  return !destinationLocode && !destinationName;
};

const getCalendarDayValue = (value: string): number | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return date.getTime();
};

/**
 * Compares the calendar date written by the shipment source with the user's
 * current calendar date. This intentionally avoids elapsed 24-hour buckets and
 * timezone conversion, keeping the relative label consistent with formatDate.
 */
export const getShipmentCalendarDayOffset = (
  value: string,
  today: Date
): number | null => {
  const targetDay = getCalendarDayValue(value);
  if (targetDay === null) return null;

  const currentDay = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  return Math.round((targetDay - currentDay) / MILLISECONDS_PER_DAY);
};

export const getPlannedShipmentStatus = (
  plannedEtd: string | null | undefined,
  plannedEta: string | null | undefined,
  today: Date
): PlannedShipmentStatus => {
  const etaDayOffset = plannedEta
    ? getShipmentCalendarDayOffset(plannedEta, today)
    : null;

  if (etaDayOffset === null) return 'missingEta';

  const etdDayOffset = plannedEtd
    ? getShipmentCalendarDayOffset(plannedEtd, today)
    : null;

  if (etdDayOffset !== null && etdDayOffset > 0) {
    return 'pendingDeparture';
  }
  if (etaDayOffset === 0) return 'expectedToday';
  if (etaDayOffset < 0) return 'etaOverdue';
  return 'inTransit';
};

const getTrackingJourneyLogisticsStatus = (
  journey: TrackingJourneyLogisticsSnapshot,
  shipment: ShipmentLogisticsSnapshot
): Exclude<ShipmentLogisticsStatus, 'missingEta'> | null => {
  if (
    journey.provider_container_status?.toUpperCase() === 'EMPTY_RETURN' ||
    journey.observation_status === 'container_returned'
  ) {
    return 'arrived';
  }

  const eventType = journey.latest_actual_event?.event_type.toUpperCase();
  if (eventType) {
    if (eventType === 'EMPTY_RETURN') return 'arrived';
    if (arrivedTrackingEvents.has(eventType)) {
      return isLatestActualEventAtDestination(journey, shipment)
        ? 'arrived'
        : 'inTransit';
    }
    if (inTransitTrackingEvents.has(eventType)) return 'inTransit';
    if (preDepartureTrackingEvents.has(eventType)) return 'pendingDeparture';
  }

  // cargo_arrived and gate_out are monotonic lifecycle projections. They can
  // refer to a transshipment port, so without a destination-matched actual
  // event they are not sufficient proof of final arrival.
  if (
    journey.observation_status === 'cargo_arrived' ||
    journey.observation_status === 'gate_out'
  ) {
    return 'inTransit';
  }

  return null;
};

/**
 * Returns one operational status for a grouped shipment. When a legacy
 * reference has several independently tracked containers, an active in-transit
 * journey takes precedence over an older completed journey. The group is only
 * considered arrived when every journey with a known state has arrived and no
 * unresolved journey remains.
 */
export const getShipmentLogisticsStatus = (
  shipment: ShipmentLogisticsSnapshot,
  today: Date
): ShipmentLogisticsStatus => {
  const journeys = shipment.tracking_journeys || [];
  const journeyStatuses = journeys.map((journey) =>
    getTrackingJourneyLogisticsStatus(journey, shipment)
  );

  if (journeyStatuses.includes('inTransit')) return 'inTransit';
  if (journeyStatuses.includes('pendingDeparture')) return 'pendingDeparture';
  if (
    journeys.length > 0 &&
    journeyStatuses.every((status) => status === 'arrived')
  ) {
    return 'arrived';
  }

  const plannedStatus = getPlannedShipmentStatus(
    shipment.ship_etd,
    shipment.ship_eta,
    today
  );

  // Without API tracking, the canonical shipment ETA is the best available
  // logistics signal. Once that date is reached, treat the shipment as
  // arrived while keeping the UI explicit that this is ETA-based.
  if (journeys.length === 0) {
    if (plannedStatus === 'expectedToday' || plannedStatus === 'etaOverdue') {
      return 'arrived';
    }
    return plannedStatus;
  }

  if (plannedStatus === 'pendingDeparture' || plannedStatus === 'missingEta') {
    return plannedStatus;
  }

  // An ETA is a plan, not proof of arrival. Until every tracked journey has an
  // actual arrival state, due-today and overdue shipments remain in transit.
  return 'inTransit';
};

const shipmentReferenceCollator = new Intl.Collator('es', {
  numeric: true,
  sensitivity: 'base',
});

const getCalendarTimestamp = (value?: string | null) =>
  value ? getCalendarDayValue(value) : null;

const getTrackedEtaTimestamp = (
  shipment: SortableShipmentLogisticsSnapshot
) => {
  const trackedEtas = (shipment.tracking_journeys || [])
    .map((journey) => getCalendarTimestamp(journey.live_eta))
    .filter((value): value is number => value !== null);

  return trackedEtas.length > 0
    ? Math.min(...trackedEtas)
    : getCalendarTimestamp(shipment.ship_eta);
};

const getArrivedTimestamp = (
  shipment: SortableShipmentLogisticsSnapshot
) => {
  const actualEventDates = (shipment.tracking_journeys || [])
    .map((journey) =>
      getCalendarTimestamp(journey.latest_actual_event?.occurred_at)
    )
    .filter((value): value is number => value !== null);

  if (actualEventDates.length > 0) return Math.max(...actualEventDates);
  return getTrackedEtaTimestamp(shipment);
};

/**
 * Default operations-first ordering for the shipment details table:
 * active shipments by their next relevant date, recent arrivals next, and
 * shipments without an ETA last. Reference number makes pagination stable.
 */
export const compareShipmentsByOperationalPriority = (
  left: SortableShipmentLogisticsSnapshot,
  right: SortableShipmentLogisticsSnapshot,
  today: Date
) => {
  const leftStatus = getShipmentLogisticsStatus(left, today);
  const rightStatus = getShipmentLogisticsStatus(right, today);
  const statusRank: Record<ShipmentLogisticsStatus, number> = {
    inTransit: 0,
    pendingDeparture: 0,
    arrived: 1,
    missingEta: 2,
  };

  const rankDifference = statusRank[leftStatus] - statusRank[rightStatus];
  if (rankDifference !== 0) return rankDifference;

  if (leftStatus === 'arrived' && rightStatus === 'arrived') {
    const settlementRank = (status?: string | null) =>
      status === 'settled' ? 1 : 0;
    const settlementDifference =
      settlementRank(left.status) - settlementRank(right.status);
    if (settlementDifference !== 0) return settlementDifference;

    const leftArrival = getArrivedTimestamp(left);
    const rightArrival = getArrivedTimestamp(right);
    if (leftArrival !== rightArrival) {
      if (leftArrival === null) return 1;
      if (rightArrival === null) return -1;
      return rightArrival - leftArrival;
    }
  } else if (
    leftStatus !== 'missingEta' &&
    rightStatus !== 'missingEta'
  ) {
    const operationalTimestamp = (
      shipment: SortableShipmentLogisticsSnapshot,
      status: ShipmentLogisticsStatus
    ) =>
      status === 'pendingDeparture'
        ? getCalendarTimestamp(shipment.ship_etd)
        : getTrackedEtaTimestamp(shipment);
    const leftDate = operationalTimestamp(left, leftStatus);
    const rightDate = operationalTimestamp(right, rightStatus);

    if (leftDate !== rightDate) {
      if (leftDate === null) return 1;
      if (rightDate === null) return -1;
      return leftDate - rightDate;
    }

    // When two active shipments share a date, show in-transit cargo first.
    if (leftStatus !== rightStatus) {
      return leftStatus === 'inTransit' ? -1 : 1;
    }
  }

  return shipmentReferenceCollator.compare(
    left.reference_number || '',
    right.reference_number || ''
  );
};

const getSortableTimestamp = (value?: Date | string | null) => {
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  }
  if (!value) return null;
  return getCalendarDayValue(value);
};

const getManualSortValue = (
  shipment: SortableShipmentLogisticsSnapshot,
  field: ShipmentManualSortField,
  today: Date
): string | number | null => {
  const primaryTracking = shipment.tracking_journeys?.[0];

  switch (field) {
    case 'client':
      return shipment.receiver?.trim() || null;
    case 'reference':
      return shipment.reference_number?.trim() || null;
    case 'container':
      return shipment.container_numbers?.[0]?.trim() || null;
    case 'kilos':
      return shipment.total_kilos ?? null;
    case 'logisticsStatus': {
      const rank: Record<ShipmentLogisticsStatus, number> = {
        pendingDeparture: 0,
        inTransit: 1,
        arrived: 2,
        missingEta: 3,
      };
      return rank[getShipmentLogisticsStatus(shipment, today)];
    }
    case 'etd':
      return getSortableTimestamp(
        primaryTracking?.live_etd || shipment.ship_etd
      );
    case 'eta':
      return getSortableTimestamp(
        primaryTracking?.live_eta || shipment.ship_eta
      );
    case 'vessel':
      return (
        primaryTracking?.latest_actual_event?.vessel_name?.trim() ||
        shipment.ship_name?.trim() ||
        null
      );
    case 'destination':
      return (
        primaryTracking?.destination_locode?.trim() ||
        primaryTracking?.destination_name?.trim() ||
        shipment.ship_destination_port?.trim() ||
        null
      );
    case 'apiTracking':
      return shipment.tracking_journeys?.length ? 1 : 0;
    case 'settlementStatus': {
      if (shipment.status === 'settled') return 2;
      const settlementDate = getSortableTimestamp(
        shipment.estimated_settlement_date
      );
      return settlementDate !== null && settlementDate < today.getTime()
        ? 1
        : 0;
    }
    case 'settlementDate':
      return getSortableTimestamp(shipment.estimated_settlement_date);
  }
};

/** Sorts one selected table field while keeping empty values last. */
export const compareShipmentsByField = (
  left: SortableShipmentLogisticsSnapshot,
  right: SortableShipmentLogisticsSnapshot,
  field: ShipmentManualSortField,
  direction: ShipmentSortDirection,
  today: Date
) => {
  const leftValue = getManualSortValue(left, field, today);
  const rightValue = getManualSortValue(right, field, today);

  if (leftValue === null && rightValue !== null) return 1;
  if (leftValue !== null && rightValue === null) return -1;

  let comparison = 0;
  if (typeof leftValue === 'number' && typeof rightValue === 'number') {
    comparison = leftValue - rightValue;
  } else if (leftValue !== null && rightValue !== null) {
    comparison = shipmentReferenceCollator.compare(
      String(leftValue),
      String(rightValue)
    );
  }

  if (comparison !== 0) {
    return direction === 'descending' ? -comparison : comparison;
  }

  return shipmentReferenceCollator.compare(
    left.reference_number || '',
    right.reference_number || ''
  );
};
